import { projectTasksPermissions } from "./tasks-permissions";

export type WorkspaceTeam = { teamKey: string; name: string };
export type WorkspaceContext = {
  user: { id?: string; name: string; email: string };
  teams: WorkspaceTeam[];
  currentTeam: WorkspaceTeam | null;
  apps: { appId: string; title: string; description: string }[];
  tasksPermissions?: string[];
  canUseAssistant?: boolean;
};
export type IntegradorSummary = {
  tenantId: number;
  user: { name: string; email: string };
  vendors: { id: number; name: string }[];
};
export type SessionFailure = { status: "login" | "denied" | "unavailable" };
export type WorkspaceResult =
  | SessionFailure
  | { status: "ready"; context: WorkspaceContext };
export type IntegradorResult =
  | SessionFailure
  | { status: "ready"; summary: IntegradorSummary };

export class RedesignSessionError extends Error {
  constructor(readonly status: number) {
    super(status === 403 ? "Session denied" : "Session request failed");
  }
}
export function isTeamKey(key: unknown): key is string {
  return typeof key === "string" && /^t_[A-Za-z0-9_-]{22}$/.test(key);
}
/** Canonical authenticated workspace identity (ABP positive Int64). */
export function canonicalWorkspaceUserId(value: unknown): string | undefined {
  if (typeof value === "number")
    return Number.isSafeInteger(value) && value > 0 ? String(value) : undefined;
  if (
    typeof value === "string" &&
    /^[1-9]\d{0,18}$/.test(value) &&
    BigInt(value) <= BigInt("9223372036854775807")
  )
    return value;
  return undefined;
}
const TEAM_ROUTE =
  /^(?:apps|agenda|relatorio|plugins|integrador(?:-v2)?(?:\/(?:estoque|arquivado|dashboard|tags|config|novo|preview-portais|veiculos\/[A-Za-z0-9_-]+))?|prototipo-[ab]|lead(?:\/[A-Za-z0-9_-]+)?|contatos(?:\/(?:novo|[A-Za-z0-9_-]+))?|tarefas(?:\/[A-Za-z0-9_-]+)?|timeline-do-cliente(?:\/(?:novo|[A-Za-z0-9_-]+))?|workspace(?:\/users(?:\/[A-Za-z0-9_-]+)?|\/trafego-pago(?:\/meta)?)?|components(?:\/[a-z0-9-]+)?)$/;
function splitTeamRoute(value: string) {
  if (!value.startsWith("/") || /[\\\s#\x00-\x1f\x7f]/.test(value)) return null;
  const queryIndex = value.indexOf("?");
  const pathname = queryIndex < 0 ? value : value.slice(0, queryIndex);
  const query = queryIndex < 0 ? "" : value.slice(queryIndex + 1);
  const segments = pathname.split("/");
  const teamKey = segments[1];
  const route = segments.slice(2).join("/").replace(/\/$/, "");
  if (!isTeamKey(teamKey) || !TEAM_ROUTE.test(route)) return null;
  return { teamKey, route, query: query ? `?${query}` : "" };
}
export function isWorkspacesPath(pathname: string) {
  return pathname === "/workspaces" || pathname.startsWith("/workspaces/");
}
export function isWorkspaceManagementPath(pathname: string) {
  return /^\/workspace-legado\/?$/.test(pathname);
}
export function isRedesignPath(pathname: string) {
  if (isWorkspacesPath(pathname) || isWorkspaceManagementPath(pathname))
    return true;
  if (/^\/[^/]+\/apps\/?$/.test(pathname)) return true;
  const [, teamKey, ...segments] = pathname.split("/");
  // Malformed t_ keys still reach server validation; preserve unrelated Pages routes.
  return (
    pathname.startsWith("/") &&
    Boolean(teamKey?.startsWith("t_")) &&
    TEAM_ROUTE.test(segments.join("/").replace(/\/$/, ""))
  );
}
function isWorkspacesReturnPath(value: string) {
  if (!value.startsWith("/") || /[\\\s#\x00-\x1f\x7f]/.test(value))
    return false;
  const pathname = value.split("?", 1)[0].replace(/\/$/, "");
  if (
    pathname === "/workspaces" ||
    pathname === "/workspaces/conta" ||
    pathname === "/workspaces/minhas-tarefas"
  )
    return true;
  const [, prefix, teamKey, ...segments] = pathname.split("/");
  const route = segments.join("/");
  return (
    prefix === "workspaces" &&
    isTeamKey(teamKey) &&
    (route === "" || route === "conta" || /^usuarios\/[1-9]\d*$/.test(route))
  );
}

export function redesignReturnTo(value: unknown) {
  if (typeof value !== "string") return "/workspaces";
  if (/^\/workspace-legado\/?(?:\?workspace=t_[A-Za-z0-9_-]{22})?$/.test(value))
    return value;
  if (
    /^\/(?:tela-do-usuario|plugins)?(?:\?workspace=t_[A-Za-z0-9_-]{22})?$/.test(
      value,
    )
  )
    return value;
  return isWorkspacesReturnPath(value) || splitTeamRoute(value)
    ? value
    : "/workspaces";
}
export function teamPath(teamKey: string, route: string) {
  if (!isTeamKey(teamKey) || !splitTeamRoute(`/${teamKey}/${route}`))
    throw new Error("Invalid team route");
  return `/${teamKey}/${route}`;
}
export function switchTeamPath(currentPath: string, nextTeamKey: string) {
  if (!isTeamKey(nextTeamKey)) throw new Error("Invalid team key");
  const current = splitTeamRoute(currentPath);
  if (!current) return teamPath(nextTeamKey, "apps");
  if (/^integrador(?:-v2)?(?:\/|$)/.test(current.route)) {
    return teamPath(
      nextTeamKey,
      /^integrador(?:-v2)?\/dashboard$/.test(current.route)
        ? "integrador/dashboard"
        : /^integrador(?:-v2)?\/arquivado$/.test(current.route)
          ? "integrador/arquivado"
          : /^integrador(?:-v2)?\/tags$/.test(current.route)
            ? "integrador/tags"
            : /^integrador(?:-v2)?\/config$/.test(current.route)
              ? "integrador/config"
              : "integrador/estoque",
    );
  }
  const detailList = /^contatos\/(?!novo$)/.test(current.route)
    ? "contatos"
    : /^tarefas\//.test(current.route)
      ? "tarefas"
      : /^timeline-do-cliente\/(?!novo$)/.test(current.route)
        ? "timeline-do-cliente"
        : /^workspace\/users\//.test(current.route)
          ? "workspace/users"
          : null;
  return teamPath(nextTeamKey, detailList ?? current.route + current.query);
}
export function redesignLoginPath(returnTo: string) {
  return `/auth/login?experience=redesign&returnTo=${encodeURIComponent(redesignReturnTo(returnTo))}`;
}
function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new RedesignSessionError(502);
  return value as Record<string, unknown>;
}
function string(value: unknown) {
  return typeof value === "string" ? value : "";
}
function list(value: unknown) {
  if (!Array.isArray(value)) throw new RedesignSessionError(502);
  return value as unknown[];
}
function team(value: unknown): WorkspaceTeam {
  const dto = record(value);
  if (!isTeamKey(dto.teamKey)) throw new RedesignSessionError(502);
  return { teamKey: dto.teamKey, name: string(dto.name) };
}
export function sanitizeWorkspaceContext(
  value: unknown,
  selectedKey?: string,
): WorkspaceContext {
  const dto = record(value),
    user = record(dto.user);
  const teams = list(dto.teams).map(team);
  let currentTeam: WorkspaceTeam | null = null;
  if (selectedKey !== undefined) {
    if (!isTeamKey(selectedKey) || !dto.currentTeam)
      throw new RedesignSessionError(403);
    currentTeam = team(dto.currentTeam);
    if (
      currentTeam.teamKey !== selectedKey ||
      !teams.some((t) => t.teamKey === selectedKey)
    )
      throw new RedesignSessionError(403);
  }
  return {
    user: {
      ...(canonicalWorkspaceUserId(user.id)
        ? { id: canonicalWorkspaceUserId(user.id) }
        : {}),
      name: string(user.name),
      email: string(user.email),
    },
    teams,
    currentTeam,
    tasksPermissions: projectTasksPermissions(
      Object.hasOwn(dto, "grantedPermissions")
        ? dto.grantedPermissions
        : dto.tasksPermissions,
    ),
    canUseAssistant: dto.canUseAssistant === true,
    apps: list(dto.apps).map((value) => {
      const app = record(value);
      return {
        appId: string(app.appId),
        title: string(app.title),
        description: string(app.description),
      };
    }),
  };
}
function failure(error: unknown): SessionFailure {
  const status = (error as { status?: number })?.status;
  return {
    status:
      status === 401
        ? "login"
        : status === 403 || status === 404
          ? "denied"
          : "unavailable",
  };
}
export async function resolveWorkspaceContext(
  token: string | undefined,
  teamKey: string | undefined,
  dependencies: {
    loadWorkspace: (token: string, teamKey?: string) => Promise<unknown>;
  },
): Promise<WorkspaceResult> {
  if (!token) return { status: "login" };
  if (teamKey !== undefined && !isTeamKey(teamKey)) return { status: "denied" };
  try {
    const response = record(await dependencies.loadWorkspace(token, teamKey));
    return {
      status: "ready",
      context: sanitizeWorkspaceContext(response.result, teamKey),
    };
  } catch (error) {
    return failure(error);
  }
}
export async function resolveIntegradorSummary(
  token: string | undefined,
  teamKey: string,
  dependencies: {
    loadIntegradorScope: (token: string, key: string) => Promise<unknown>;
    switchTenant: (
      token: string,
      tenantId: number,
    ) => Promise<{ accessToken: string }>;
    loadIntegrador: (token: string) => Promise<unknown>;
  },
): Promise<IntegradorResult> {
  if (!token) return { status: "login" };
  if (!isTeamKey(teamKey)) return { status: "denied" };
  try {
    const scope = record(
      record(await dependencies.loadIntegradorScope(token, teamKey)).result,
    );
    const tenantId = scope.tenantId;
    const vendorIds = list(scope.vendorIds);
    if (
      typeof tenantId !== "number" ||
      !Number.isSafeInteger(tenantId) ||
      tenantId <= 0 ||
      tenantId > 2147483647 ||
      !vendorIds.length ||
      vendorIds.some(
        (id) => typeof id !== "number" || !Number.isSafeInteger(id) || id <= 0,
      )
    )
      throw new RedesignSessionError(403);
    const projected = await dependencies.switchTenant(token, tenantId);
    if (typeof projected.accessToken !== "string" || !projected.accessToken)
      throw new RedesignSessionError(502);
    const session = record(
      record(await dependencies.loadIntegrador(projected.accessToken)).result,
    );
    if (session.tenantId !== tenantId) throw new RedesignSessionError(403);
    const user = record(session.user);
    const allowed = new Set(vendorIds);
    const vendors = list(session.userLinkedVendors)
      .map(record)
      .filter((vendor) => allowed.has(vendor.id))
      .map((vendor) => ({
        id: vendor.id as number,
        name:
          string(vendor.displayName) ||
          string(vendor.tradeName) ||
          string(vendor.businessName),
      }));
    return {
      status: "ready",
      summary: {
        tenantId,
        user: { name: string(user.name), email: string(user.emailAddress) },
        vendors,
      },
    };
  } catch (error) {
    return failure(error);
  }
}
